import { testingAccessGuard } from './guards/testing-access.guard';
import { LayoutComponent } from './pages/layout.component';
import { LoginComponent } from './components/login/login.component';
import { Routes } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import { QbRedirectComponent } from './pages/quickbooks/qb-redirect/qb-redirect.component';
import { QuickbooksResolver } from './pages/quickbooks/quickbooks.resolver';
import { ProfileResolver } from './pages/profile/profile.resolver';
import { ForbiddenComponent } from './components/forbidden/forbidden.component';
import { reportsAccessGuard } from './guards/reports-access.guard';
import { hasUnsavedChangesGuard } from './guards/has-unsaved-changes.guard';
import { RECREATE_ON_MENU_PICK } from './routing/recreate-on-param-change.strategy';

export const routes: Routes = [
  { path: 'login', component: LoginComponent },
  { path: 'qb-redirect-url', component: QbRedirectComponent },
  { path: 'forbidden', component: ForbiddenComponent },
  {
    path: '',
    component: LayoutComponent,
    resolve: { qbData: QuickbooksResolver },
    canActivate: [authGuard],
    children: [
      {
        path: 'testing',
        canActivate: [testingAccessGuard],
        canDeactivate: [hasUnsavedChangesGuard],
        loadComponent: () => import('./pages/testing/testing.component').then((m) => m.TestingComponent),
      },
      {
        path: 'profile',
        resolve: { data: ProfileResolver },
        loadComponent: () => import('./pages/profile/profile.component').then((m) => m.ProfileComponent),
      },
      {
        path: 'admins',
        loadChildren: () => import('./pages/admins/admins.routes').then((m) => m.adminsRoutes),
      },
      {
        path: 'reps',
        loadChildren: () => import('./pages/reps/reps.routes').then((m) => m.repsRoutes),
      },
      {
        path: 'orders',
        data: { [RECREATE_ON_MENU_PICK]: true },
        loadComponent: () => import('./pages/orders/orders.component').then((m) => m.OrdersComponent),
      },
      {
        path: 'invoices',
        loadChildren: () => import('./pages/invoices/invoices.routes').then((m) => m.invoicesRoutes),
      },
      {
        path: 'payments/new',
        loadComponent: () =>
          import('./pages/payments/payment-edit/payment-edit.component').then((m) => m.PaymentEditComponent),
        canDeactivate: [hasUnsavedChangesGuard],
      },
      {
        path: 'payments/:id/edit',
        loadComponent: () =>
          import('./pages/payments/payment-edit/payment-edit.component').then((m) => m.PaymentEditComponent),
        canDeactivate: [hasUnsavedChangesGuard],
      },
      {
        path: 'payments',
        data: { [RECREATE_ON_MENU_PICK]: true },
        loadComponent: () => import('./pages/payments/payments.component').then((m) => m.PaymentsComponent),
      },
      {
        path: 'service-slips',
        data: { [RECREATE_ON_MENU_PICK]: true },
        loadComponent: () =>
          import('./pages/service-slips/service-slips.component').then((m) => m.ServiceSlipsComponent),
      },
      {
        path: 'service-slips/create',
        loadComponent: () =>
          import('./pages/service-slips/service-slips-edit/service-slips-edit.component').then(
            (m) => m.ServiceSlipsEditComponent,
          ),
        data: { mode: 'create' },
        canDeactivate: [hasUnsavedChangesGuard],
      },
      {
        path: 'service-slips/:id/edit',
        loadComponent: () =>
          import('./pages/service-slips/service-slips-edit/service-slips-edit.component').then(
            (m) => m.ServiceSlipsEditComponent,
          ),
        canDeactivate: [hasUnsavedChangesGuard],
      },
      {
        path: 'rentals',
        data: { [RECREATE_ON_MENU_PICK]: true },
        loadComponent: () => import('./pages/rentals/rentals.component').then((m) => m.RentalsComponent),
      },
      {
        path: 'rentals/create',
        loadComponent: () =>
          import('./pages/rentals/rental-editor/rental-editor.component').then((m) => m.RentalEditorComponent),
        canDeactivate: [hasUnsavedChangesGuard],
      },
      {
        path: 'rentals/:id/edit',
        loadComponent: () =>
          import('./pages/rentals/rental-editor/rental-editor.component').then((m) => m.RentalEditorComponent),
        canDeactivate: [hasUnsavedChangesGuard],
      },
      {
        path: 'transfers',
        data: { [RECREATE_ON_MENU_PICK]: true },
        loadComponent: () => import('./pages/transfers/transfers.component').then((m) => m.TransfersComponent),
      },
      {
        path: 'transfers/create',
        loadComponent: () =>
          import('./pages/transfers/transfer-editor/transfer-editor.component').then((m) => m.TransferEditorComponent),
        canDeactivate: [hasUnsavedChangesGuard],
      },
      {
        path: 'transfers/:id/edit',
        loadComponent: () =>
          import('./pages/transfers/transfer-editor/transfer-editor.component').then((m) => m.TransferEditorComponent),
        canDeactivate: [hasUnsavedChangesGuard],
      },
      {
        path: 'rental-machines',
        loadComponent: () =>
          import('./pages/rental-machines/rental-machines.component').then((m) => m.RentalMachinesComponent),
      },
      {
        path: 'manual-invoice-sync',
        loadComponent: () =>
          import('./pages/manual-invoice-sync/manual-invoice-sync.component').then((m) => m.ManualInvoiceSyncComponent),
      },
      {
        path: 'customers',
        loadChildren: () => import('./pages/customers/customers.routes').then((m) => m.customersRoutes),
      },
      {
        path: 'products',
        loadChildren: () => import('./pages/products/products.routes').then((m) => m.productsRoutes),
      },
      {
        path: 'pricing-groups',
        loadChildren: () => import('./pages/pricing-groups/pricing-groups.routes').then((m) => m.pricingGroupsRoutes),
      },
      {
        path: 'territory-management',
        loadComponent: () =>
          import('./pages/territory-management/territory-management.component').then(
            (m) => m.TerritoryManagementComponent,
          ),
      },
      {
        path: 'reports',
        canActivate: [reportsAccessGuard],
        loadComponent: () => import('./pages/reports/reports.component').then((m) => m.ReportsComponent),
      },
      {
        // AR Reports is a section with one report today and four more named in its tab strip, so it
        // loads child routes rather than a single component.
        path: 'insights/ar-reports',
        loadChildren: () => import('./pages/ar-reports/ar-reports.routes').then((m) => m.arReportsRoutes),
      },
      {
        path: 'quickbooks',
        loadComponent: () => import('./pages/quickbooks/quickbooks.component').then((m) => m.QuickbooksComponent),
        resolve: { data: QuickbooksResolver },
      },

      { path: '', redirectTo: 'invoices', pathMatch: 'full' },
    ],
  },
  { path: '**', redirectTo: '', pathMatch: 'full' },
];
